import type { CreatePricePluginProps } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm'
import type { MaxPrice } from '@domains/subscription/priv/Billables/usecases/Price/max/max'
import { isMax } from '@domains/subscription/priv/Billables/usecases/Price/max/maxMapper'
import type { BaseAlgorithm, BasePrice } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type { PaymentFrequencyRequest } from '@domains/subscription/priv/types'
import { Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography, useTheme } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'

export interface MaxPriceFormProps extends CreatePricePluginProps {
  getAlgorithmPlugin: (algorithm: BaseAlgorithm) => React.ComponentType<CreatePricePluginProps> | undefined
}

export const MaxPriceForm = ({
  price: unknownPrice,
  getAlgorithmPlugin,
  additionalPlugins,
  onCreatedPrice,
  billableId,
  ...props
}: MaxPriceFormProps) => {
  const theme = useTheme()

  const price = defaultMaxPrice({ price: unknownPrice, billableId: billableId })

  const form = useForm({ values: price })

  const LeftAlgorithm = getAlgorithmPlugin(price.lalgorithm) ?? MissingPlugin
  const RightAlgorithm = getAlgorithmPlugin(price.ralgorithm) ?? MissingPlugin

  const availablePlugins = additionalPlugins?.map((plugin) => plugin.type) ?? []

  const defaultPaymentFrequency: PaymentFrequencyRequest = price?.paymentFrequency ?? { type: 'anchorable', period: 'monthly' }

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <form onSubmit={form.handleSubmit((data) => onCreatedPrice(data))}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Stack
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Typography variant="p2Medium">Left Algorithm</Typography>
              <FormControl>
                <InputLabel>Price type</InputLabel>
                <Controller
                  control={form.control}
                  name="lalgorithm.type"
                  render={({ field }) => (
                    <Select
                      size="small"
                      slotProps={{ input: { 'aria-label': 'left-algorithm-price-type' } }}
                      label="Price type"
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                    >
                      {availablePlugins.map((pluginType) => (
                        <MenuItem key={pluginType} value={pluginType}>
                          {pluginType}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Stack>
            <Paper sx={{ backgroundColor: theme.palette.grey[100], p: 2 }}>
              <LeftAlgorithm
                price={price.lalgorithm ? { ...price.lalgorithm, paymentFrequency: defaultPaymentFrequency } : undefined}
                billableId={billableId}
                {...props}
                onPriceChange={(data) => {
                  form.setValue(`lalgorithm`, data)
                }}
                onCreatedPrice={noop}
                mode="nested"
              />
            </Paper>
          </Stack>
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Stack
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Typography variant="p2Medium">Right Algorithm</Typography>
              <FormControl>
                <InputLabel>Price type</InputLabel>
                <Controller
                  control={form.control}
                  name="ralgorithm.type"
                  render={({ field }) => (
                    <Select
                      size="small"
                      slotProps={{ input: { 'aria-label': 'right-algorithm-price-type' } }}
                      label="Price type"
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                    >
                      {availablePlugins.map((pluginType) => (
                        <MenuItem key={pluginType} value={pluginType}>
                          {pluginType}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Stack>
            <Paper sx={{ backgroundColor: theme.palette.grey[100], p: 2 }}>
              <RightAlgorithm
                price={price.ralgorithm ? { ...price.ralgorithm, paymentFrequency: defaultPaymentFrequency } : undefined}
                billableId={billableId}
                {...props}
                onPriceChange={(data) => {
                  form.setValue(`ralgorithm`, data)
                }}
                onCreatedPrice={noop}
                mode="nested"
              />
            </Paper>
          </Stack>
          <Button type="submit" variant="contained">
            Save max price
          </Button>
        </Stack>
      </form>
    </Stack>
  )
}

interface DefaultTieredPriceParams {
  price: BasePrice | undefined
  billableId: string
}

const defaultMaxPrice = ({ price, billableId }: DefaultTieredPriceParams): MaxPrice<BaseAlgorithm> => {
  if (isMax(price)) return price

  return {
    type: 'Max',
    lalgorithm: { type: 'Linear' },
    ralgorithm: { type: 'Linear' },
    billableId,
    paymentFrequency: price?.paymentFrequency ?? { type: 'fixed', frequency: 'monthly', dayOfMonth: 28 },
    supported: true,
  }
}

const MissingPlugin = ({ price }: { price: BasePrice | undefined }) => (
  <Stack
    data-testid="missing-max-plugin"
    sx={{
      gap: 2,
    }}
  >
    <Typography variant="p4Regular" sx={{ color: 'red' }}>
      Backoffice does not support '{price?.type}' price types in this configuration
    </Typography>
  </Stack>
)

const noop = () => {}
